import {
  Activity, AlertTriangle, ArrowLeftRight, BarChart2,
  BellRing, BookMarked, BookOpen, BookText, BookUser, Bot,
  Brain, Building2, Calculator, Calendar, CheckCircle2,
  ClipboardList, Code2, Compass, CreditCard, Database, DollarSign,
  FileDown, FileEdit, FileText, Filter, Fingerprint, GitCompareArrows,
  GitMerge, History, Inbox, KeyRound, Layers, Lightbulb,
  LineChart, Lock, MapPin, Megaphone, MessageSquare,
  Package, Package2, PenLine, PlusCircle,
  Percent, Printer, RefreshCw, ScanSearch, Send, Settings2, PackageCheck,
  ShoppingBag, ShoppingCart, Ship, Store, TrendingUp, Truck,
  Upload, UserCheck, Users, Warehouse, Zap, TrendingDown,
  Network, Bell,
} from 'lucide-react';
import type { NavItemConfig, NavSectionConfig } from './types';

/** A warehouse we run ourselves, as the nav needs it. */
export interface OwnWarehouseNavEntry {
  code: string;
  name: string;
}

const BASE_SECTIONS: NavSectionConfig[] = [
  {
    items: [
      { to: '/analytics', label: 'Analytics', icon: BarChart2 },
      { to: '/ops-inbox', label: 'Ops Inbox', icon: Bell },
      { to: '/ai-chat', label: 'AI Assistant', icon: Bot },
      { to: '/system-atlas', label: 'System Atlas', icon: Network },
    ],
  },
  {
    label: 'Fleet',
    icon: Bot,
    items: [
      { to: '/fleet/console', label: 'Console', icon: Bot },
      { to: '/fleet', label: 'Overview', icon: BarChart2, end: true },
      { to: '/fleet/escalations', label: 'Escalations', icon: BellRing, badgeSource: 'fleet-escalations' },
      { to: '/fleet/activity', label: 'Activity', icon: Activity },
      { to: '/fleet/tasks', label: 'Tasks', icon: ClipboardList },
      { to: '/fleet/agents', label: 'Agents', icon: Bot },
      { to: '/fleet/trace', label: 'Trace viewer', icon: Network },
      { to: '/fleet/bounds', label: 'Bounds & policy', icon: Settings2 },
      { to: '/fleet/runtime', label: 'Runtime', icon: Zap },
      { to: '/fleet/settings', label: 'Settings', icon: Settings2, end: true },
    ],
  },
  {
    label: 'B2C Sales',
    icon: ShoppingCart,
    items: [
      { to: '/sales', label: 'B2C Sales', icon: ShoppingCart },
      { to: '/ads', label: 'Ads Analytics', icon: Megaphone },
      { to: '/marketing', label: 'Marketing Assistant', icon: Megaphone },
    ],
  },
  {
    label: 'Inventory',
    icon: Package,
    items: [
      { to: '/inventory', label: 'Inventory', icon: Package },
      { to: '/inventory-health', label: 'Inventory Health', icon: Activity },
      { to: '/stock-flows', label: 'Stock Flows', icon: ArrowLeftRight },
      { to: '/stock-flows/traceability', label: 'Invoice Traceability', icon: GitMerge },
      { to: '/bundles/collate', label: 'Bundle collation', icon: Layers },
    ],
  },
  {
    label: 'Procurement',
    icon: ClipboardList,
    // Rebuilt to the v3 Shopify-Polaris design. Orders is the single inbound
    // view (trade/print-run/bundle/transfer); Proposed, Goods-to-arrive and
    // Transfers are stages/type filters inside it, not separate pages.
    items: [
      // ── Core ─────────────────────────────────────────────────────
      { to: '/order-management/orders', label: 'Orders', icon: ClipboardList, activeSearch: '' },
      { to: '/order-management/invoices', label: 'Invoices', icon: FileText },
      // ── Diagnostics & comms ──────────────────────────────────────
      { to: '/order-management/edi', label: 'EDI status', icon: Inbox },
      { to: '/order-management/files', label: 'Files inbox', icon: FileDown },
      { to: '/order-management/3pl', label: '3PL inventory', icon: Warehouse },
      { to: '/order-management/scorecard', label: 'Supplier scorecard', icon: ScanSearch },
      { to: '/order-management/publishers', label: 'Publisher discussions', icon: MessageSquare },
      { to: '/negotiation-logging', label: 'Negotiations', icon: Percent },
      { to: '/historical-purchases', label: 'Historical buying', icon: History },
      // ── Tools & builders ─────────────────────────────────────────
      { to: '/order-management/orders?type=print_run', label: 'Print runs', icon: Printer, activeSearch: 'type=print_run' },
      { to: '/order-management/orders?type=transfer', label: 'Warehouse transfers', icon: ArrowLeftRight, activeSearch: 'type=transfer' },
      { to: '/purchasing/sourcing', label: 'Book sourcing', icon: BookMarked },
      { to: '/purchasing/carton-engine', label: 'Purchase engine (UK)', icon: Settings2 },
    ],
  },
  {
    label: 'Data',
    icon: Database,
    items: [
      { to: '/all-books', label: 'All Books', icon: BookOpen },
      { to: '/amazon-pulls', label: 'Amazon Data & BSR', icon: TrendingUp },
      { to: '/distributor-stock', label: 'Distributor Stock', icon: Warehouse },
      { to: '/order-predictor', label: 'Order Predictor', icon: Brain },
      { to: '/manual-product-import', label: 'Manual Product Import', icon: PlusCircle },
      { to: '/manual-uploads', label: 'Manual Data Uploads', icon: Upload },
      { to: '/lookup', label: 'Bulk Lookup', icon: BookUser },
      { to: '/sku-searcher', label: 'SKU Searcher', icon: ScanSearch },
      { to: '/nielsen-bookscan-drop', label: 'Nielsen Bookscan Drop', icon: FileDown },
      { to: '/keepa', label: 'Keepa Dashboard', icon: Activity },
      { to: '/bsr-curves', label: 'BSR Curves', icon: LineChart },
    ],
  },
  {
    label: 'Amazon',
    icon: ShoppingBag,
    items: [
      { to: '/amazon/restock', label: 'Restock', icon: Send },
      { to: '/amazon/pipeline', label: 'Pipeline', icon: Truck },
      { to: '/amazon/shipments', label: 'Shipments', icon: PackageCheck },
      { to: '/amazon/advisor', label: 'FBA Advisor', icon: Lightbulb },
      { to: '/amazon/wholesale-fba', label: 'Wholesale Stock to FBA', icon: PackageCheck },
      { to: '/amazon/warehouse-fba', label: 'Warehouse Stock to FBA', icon: PackageCheck },
      { to: '/amazon/create-listing', label: 'Create Listing', icon: PlusCircle },
      { to: '/amazon/sellersnap', label: 'SellerSnap Pricing', icon: DollarSign },
      { to: '/amazon/alerts', label: 'Restock Alerts', icon: BellRing },
      {
        label: 'Settings',
        icon: Settings2,
        children: [
          { to: '/amazon/settings', label: 'General', icon: Settings2 },
          { to: '/amazon/settings/lead-times', label: 'Lead Times', icon: Calendar },
        ],
      },
    ],
  },
  {
    label: 'Wholesale',
    icon: Building2,
    items: [
      { to: '/wholesale/orders', label: 'Orders', icon: Zap },
      { to: '/wholesale/catalogue', label: 'Catalogue', icon: BookOpen },
      { to: '/wholesale/customers', label: 'All Customers', icon: Users },
      { to: '/wholesale/orders?view=customers', label: 'Customers (open work)', icon: Users, activeSearch: 'view=customers' },
      { to: '/wholesale/fulfillment', label: 'Fulfillment', icon: PackageCheck },
      {
        // Hub, pipeline, and day-to-day ops under /wholesale/operations (and related routes).
        label: 'Operations',
        icon: Calendar,
        children: [
          { to: '/wholesale/operations', label: 'Operations', icon: Calendar },
          { to: '/wholesale/operations?tab=metrics', label: 'Pipeline', icon: BarChart2, activeSearch: 'tab=metrics' },
          { to: '/fleet/console', label: 'Ops console', icon: Bot },
          { to: '/wholesale/cockpit', label: 'Cockpit (quotes)', icon: Compass },
          { to: '/wholesale/cockpit/proposals', label: 'Cockpit proposals', icon: Inbox },
          { to: '/wholesale/shipments', label: 'Shipments', icon: PackageCheck },
        ],
      },
      {
        label: 'Customers',
        icon: Users,
        children: [
          { to: '/wholesale/customers', label: 'All Customers', icon: Users },
          { to: '/wholesale/customer-sales-tracker', label: 'Sales Tracker', icon: UserCheck },
        ],
      },
      {
        label: 'Catalogue & Outreach',
        icon: BookOpen,
        children: [
          { to: '/wholesale/catalogue', label: 'Catalogue', icon: BookOpen },
          { to: '/wholesale/catalogue/download', label: 'Download Catalogue', icon: FileDown },
          { to: '/wholesale/advisor', label: 'Advisor', icon: Compass },
        ],
      },
      { to: '/wholesale/sourcing', label: 'Sourcing', icon: Package },
      {
        label: 'Financials',
        icon: CreditCard,
        children: [
          { to: '/wholesale/profitability', label: 'Profitability', icon: TrendingUp },
          { to: '/wholesale/kalonomia-invoices', label: 'Kalonomia Invoices', icon: KeyRound },
          { to: '/wholesale/cockpit/pricing-admin', label: 'Pricing Admin', icon: Settings2 },
          { to: '/wholesale/3pl-invoices', label: '3PL Invoices', icon: Warehouse },
          { to: '/wholesale/repricing/runs', label: 'Repricing', icon: TrendingDown },
        ],
      },
      { to: '/wholesale/exclusivity', label: 'Exclusivity', icon: Lock },
      {
        label: 'Post-Conversion',
        icon: RefreshCw,
        children: [
          { to: '/wholesale/post-conversion', label: 'Overview', icon: RefreshCw },
          { to: '/wholesale/post-conversion/risk', label: 'Risk Queue', icon: AlertTriangle },
          { to: '/wholesale/post-conversion/funnel', label: 'CRM Funnel', icon: Filter },
          { to: '/wholesale/post-conversion/match-review', label: 'Match Review', icon: GitCompareArrows },
        ],
      },
    ],
  },
  // {
  //   label: 'Publishers (PRM)',
  //   items: [
  //     { to: '/prm/dashboard', label: 'Dashboard', icon: BarChart2 },
  //     { to: '/prm/publishers', label: 'Publishers', icon: Building2 },
  //     { to: '/prm/distributors', label: 'Distributors', icon: Truck },
  //     { to: '/prm/contacts', label: 'Contacts', icon: Users },
  //     { to: '/prm/events', label: 'Events', icon: Calendar },
  //     { to: '/prm/email-review', label: 'Email Review', icon: Mail },
  //     { to: '/prm/email-accounts', label: 'Email Accounts', icon: AtSign },
  //     { to: '/prm/reprints', label: 'Reprint Tracker', icon: RefreshCw },
  //     { to: '/publisher-discounts', label: 'Publisher Discounts', icon: Percent },
  //   ],
  // },
  {
    label: 'Cashflow',
    icon: DollarSign,
    items: [
      { to: '/cashflow', label: 'Ledger', icon: BookText, end: true },
      { to: '/cashflow/counterparties', label: 'Counterparties', icon: Users },
      {
        to: '/cashflow/va-payments',
        label: 'VA Payments',
        icon: CreditCard,
        permission: 'va-payments',
      },
    ],
  },
  {
    label: 'Auditing',
    icon: CheckCircle2,
    items: [
      { to: '/auditing/revolut', label: 'Revolut Statements', icon: CreditCard },
      { to: '/auditing/amazon', label: 'Amazon Financial Events', icon: DollarSign },
      { to: '/auditing/ebay', label: 'eBay Financial Events', icon: ShoppingBag },
      { to: '/auditing/shopify', label: 'Shopify Financial Events', icon: Store },
      { to: '/auditing/easy-uploads', label: 'Easy Uploads', icon: Upload },
    ],
  },
  {
    label: 'Shopify',
    icon: Store,
    items: [
      { to: '/shopify-create', label: 'Create Listing', icon: PlusCircle },
      { to: '/shopify-edit', label: 'Edit Listing', icon: PenLine },
    ],
  },
  {
    label: 'Freight',
    icon: Ship,
    items: [
      { to: '/freight', label: 'Pallet Builder', icon: Ship },
      {
        label: 'Logistics',
        icon: Truck,
        children: [
          { to: '/freight/logistics', label: 'Dashboard', icon: BarChart2, end: true },
          { to: '/freight/logistics/shipments', label: 'Shipments', icon: Package },
          { to: '/freight/logistics/demand', label: 'Demand Pool', icon: Inbox },
          { to: '/freight/logistics/rates', label: 'Rates & Quoting', icon: DollarSign },
          { to: '/freight/logistics/invoices', label: 'Carrier Invoices', icon: FileText },
          { to: '/freight/logistics/claims', label: 'Claims', icon: AlertTriangle },
          { to: '/freight/logistics/journey', label: 'Journey Lookup', icon: GitMerge },
          { to: '/freight/logistics/settings', label: 'Settings', icon: Settings2 },
          { to: '/freight/logistics/accounting', label: 'Accounting', icon: Calculator },
          { to: '/freight/logistics/locations', label: 'Locations', icon: MapPin },
        ],
      },
    ],
  },
  {
    label: 'Warehousing',
    icon: Warehouse,
    items: [
      { to: '/warehousing', label: 'Overview', icon: Layers, end: true },
      {
        label: 'Argents',
        icon: Warehouse,
        children: [
          { to: '/warehousing/argents', label: 'Overview', icon: BarChart2, activeSearch: '' },
          { to: '/warehousing/argents?tab=products', label: 'Products Registry', icon: Package, activeSearch: 'tab=products' },
          { to: '/warehousing/argents?tab=asns', label: 'Inbound ASNs', icon: PackageCheck, activeSearch: 'tab=asns' },
          { to: '/warehousing/argents?tab=orders', label: 'Outbound Orders', icon: Truck, activeSearch: 'tab=orders' },
          { to: '/warehousing/argents?tab=rates', label: 'Rate Card', icon: DollarSign, activeSearch: 'tab=rates' },
        ],
      },
      {
        label: 'OPLOG',
        icon: Package2,
        children: [
          { to: '/warehousing/oplog', label: 'Overview', icon: BarChart2, activeSearch: '' },
          { to: '/warehousing/oplog?tab=orders', label: 'Orders', icon: ShoppingBag, activeSearch: 'tab=orders' },
          { to: '/warehousing/oplog?tab=stock', label: 'Stock', icon: Package, activeSearch: 'tab=stock' },
          { to: '/warehousing/oplog?tab=inbound', label: 'Inbound Receiving', icon: Inbox, activeSearch: 'tab=inbound' },
          { to: '/warehousing/oplog?tab=rates', label: 'Rate Card', icon: DollarSign, activeSearch: 'tab=rates' },
          { to: '/oplog-invoices', label: 'OPLOG Invoices', icon: Warehouse },
        ],
      },
      { to: '/warehousing/transfers', label: 'Transfers', icon: ArrowLeftRight },
      { to: '/commercial-invoices', label: 'Commercial Invoices', icon: FileText },
    ],
  },
  {
    label: 'Bundles',
    icon: Package2,
    items: [
      { to: '/bundles', label: 'Overview', icon: Package },
      { to: '/bundles/catalog', label: 'Catalog', icon: ScanSearch },
      { to: '/bundles/required', label: 'Required', icon: PackageCheck },
      { to: '/bundles/create', label: 'Create', icon: PlusCircle },
      { to: '/bundles/edit', label: 'Edit', icon: FileEdit },
      { to: '/bundles/collate', label: 'Collate', icon: Layers },
      { to: '/bundles/collation-operations', label: 'Operations', icon: ClipboardList },
      { to: '/bundles/bulk-upload', label: 'Bulk upload', icon: Upload },
    ],
  },
  {
    label: 'Settings',
    icon: Settings2,
    items: [
      { to: '/change-password', label: 'Change Password', icon: KeyRound },
      { to: '/settings/users', label: 'User Management', icon: Users },
      { to: '/settings/password-policy', label: 'Password Policy', icon: Lock },
      { to: '/settings/passkeys', label: 'Passkeys', icon: Fingerprint },
      { to: '/api-docs', label: 'API Reference', icon: Code2 },
    ],
  },
];

/**
 * The nav, with one entry per warehouse we run ourselves.
 *
 * Argents and OPLOG stay written down: each is a bespoke page built against
 * that provider's API. Sites we run ourselves are not — they share one screen
 * and differ only by code — so they are read from the registry at render time.
 * Opening another one adds its entry here without an edit, and no warehouse is
 * named in this file.
 */
export function buildNavSections(
  ownWarehouses: OwnWarehouseNavEntry[] = [],
): NavSectionConfig[] {
  if (ownWarehouses.length === 0) return BASE_SECTIONS;

  const ownItems: NavItemConfig[] = ownWarehouses.map((warehouse) => ({
    to: `/warehousing/own/${encodeURIComponent(warehouse.code)}`,
    label: warehouse.name,
    icon: Warehouse,
  }));

  return BASE_SECTIONS.map((section) => {
    if (section.label !== 'Warehousing') return section;
    // Insert before Transfers so the warehouses sit together, above the
    // cross-warehouse tools.
    const transfersAt = section.items.findIndex(
      (item) => item.to === '/warehousing/transfers',
    );
    const at = transfersAt === -1 ? section.items.length : transfersAt;
    return {
      ...section,
      items: [
        ...section.items.slice(0, at),
        ...ownItems,
        ...section.items.slice(at),
      ],
    };
  });
}

/** The nav with no own-operated warehouses — the static shape. */
export const NAV_SECTIONS: NavSectionConfig[] = BASE_SECTIONS;
